/**
 * Telegram Channel Configuration Interfaces
 *
 * These interfaces define the structure of Telegram channel configurations
 * stored in Firestore at: botsData/telegram/channels/{channelId}
 */

/**
 * Alert types supported by channels
 */
export type AlertType = "sports_betting" | "forex" | "synthetic_indices";

/**
 * Time units for kick configuration
 */
export type KickAfterUnit = "day" | "week" | "month" | "year";

/**
 * Channel types
 */
export type ChannelType = "service" | "networkMarketing";

/**
 * Monitoring configuration for a channel
 * This is a nested object in the channel document
 */
export interface MonitoringConfig {
  // Processing settings
  processImages: boolean;
  processVideos: boolean;
  autoExportToBigQuery: boolean;

  // AI settings
  aiModel: string; // e.g., "gemini-2.0-flash", "gemini-1.5-pro"
  confidenceThreshold: number; // 0-1, minimum confidence to accept AI results

  // Cache settings
  messagesCacheDuration: number; // Days to keep messages in Firestore

  // Read-only metrics (managed by system)
  lastMonitoredMessageId?: number;
  lastMonitoredAt?: Date | string | null;
  totalMonitoredMessages: number;
}

/**
 * Complete Telegram Channel configuration
 * Stored at: botsData/telegram/channels/{channelId}
 */
export interface TelegramChannel {
  // Basic identification
  id: string; // Telegram channel ID (numeric ID)
  name: string; // Human-readable channel name
  active: boolean; // Whether channel is active

  // Access control
  availableForPackage: string[]; // Package IDs that can access this channel
  type?: ChannelType; // Type of channel (service or network marketing)
  minRank?: string; // Minimum rank required (for networkMarketing)
  minAdminRank?: string; // Minimum admin rank required (for networkMarketing)
  visibleWhenRestricted?: boolean; // Whether to show as locked or hide completely when unrestricted

  // Kick configuration (auto-removal of expired users)
  kickUsers?: boolean; // Enable/disable automatic user kicking
  kickAfter?: number; // Number of time units before kicking
  kickAfterUnit?: KickAfterUnit; // Time unit for kick expiration

  // Monitoring configuration
  monitoringEnabled?: boolean; // Enable/disable message monitoring
  alertsType?: AlertType; // Type of alerts in this channel
  monitoringConfig?: MonitoringConfig; // Detailed monitoring settings

  // Metadata & Audit
  deleted?: boolean; // Logic deletion flag
  metadata?: {
    createdAt: Date | string | any;
    createdBy: string;
    updatedAt: Date | string | any;
    updatedBy: string;
    deletedAt?: Date | string | any;
    deletedBy?: string;
  };

  // System status
  lastFullCleanup?: Date | string | null;

  // Sorting
  priority?: number; // Sorting priority (higher = first)
}

/**
 * Simplified channel data for list views
 */
export interface TelegramChannelListItem {
  id: string;
  name: string;
  active: boolean;
  packagesCount: number;
  monitoringEnabled: boolean;
  kickUsers: boolean;
}

/**
 * Request/Response types for Cloud Functions
 */
export interface ListChannelsRequest {
  activeOnly?: boolean;
  includeMonitoringStats?: boolean;
}

export interface ListChannelsResponse {
  channels: TelegramChannel[];
  total: number;
}

export interface GetChannelRequest {
  channelId: string;
}

export interface GetChannelResponse {
  channel: TelegramChannel;
}

export interface UpdateChannelRequest {
  channelId: string;
  updates: Partial<TelegramChannel>;
}

export interface UpdateChannelResponse {
  success: boolean;
  channel: TelegramChannel;
}

export interface CreateChannelRequest {
  channel: Omit<TelegramChannel, "lastFullCleanup">;
}

export interface CreateChannelResponse {
  success: boolean;
  channelId: string;
  channel: TelegramChannel;
}

export interface DeleteChannelRequest {
  channelId: string;
}

export interface DeleteChannelResponse {
  success: boolean;
}

export interface UpdateChannelPrioritiesRequest {
  updates: {
    channelId: string;
    priority: number;
  }[];
}

export interface ManageUserBanRequest {
  channelId: string;
  username: string;
  action: "ban" | "unban";
  reason?: string;
}

export interface ManageUserBanResponse {
  success: boolean;
}

export interface ListChannelUsersRequest {
  channelId: string;
  status?:
    | "member"
    | "banned"
    | "kicked"
    | "left"
    | "administrator"
    | "creator"
    | "restricted"
    | "history";
  limit?: number;
  startAfter?: string; // ID of the last user document
}

export interface ChannelUser {
  usernamePK: string; // GTR Username (document ID)
  userId: number; // Telegram User ID
  firstName?: string; // Telegram First Name
  lastName?: string; // Telegram Last Name
  tgUsername?: string; // Telegram Handle (@username)
  status: string;
  joinedAt?: Date | string | any;
  bannedAt?: Date | string | any;
  kickedAt?: Date | string | any;
  leftAt?: Date | string | any;
  endDate?: Date | string | any;
  metadata?: any;
  currentPackage?: string;
  // GTR Profile Data (joined)
  profile?: {
    nickname?: string;
    photo?: string;
    rank?: string;
  };
  subscriptionEndDate?: Date | string | any;
}

export interface ListChannelUsersResponse {
  users: ChannelUser[];
  lastDocId?: string; // For pagination
  total?: number; // Optional, might be expensive
}

/**
 * Default monitoring configuration
 */
export const DEFAULT_MONITORING_CONFIG: MonitoringConfig = {
  processImages: true,
  processVideos: true,
  autoExportToBigQuery: true,
  aiModel: "gemini-2.5-flash",
  confidenceThreshold: 0.7,
  messagesCacheDuration: 360,
  totalMonitoredMessages: 0,
};

/**
 * Validation helper
 */
export function validateChannel(channel: Partial<TelegramChannel>): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  // visibleWhenRestricted is optional and boolean, no strict complex validation needed

  if (!channel.id) errors.push("Channel ID is required");
  if (!channel.name) errors.push("Channel name is required");
  if (channel.active === undefined) errors.push("Active status is required");
  if (!channel.availableForPackage) {
    errors.push("Available packages array is required");
  } else if (!Array.isArray(channel.availableForPackage)) {
    errors.push("Available packages must be an array");
  }

  // Validate kick configuration
  if (channel.kickUsers) {
    if (
      channel.kickAfter === undefined ||
      channel.kickAfter === null ||
      channel.kickAfter < 0
    ) {
      errors.push(
        "Kick after value must be greater or equal than 0 when kickUsers is enabled"
      );
    }

    if (!channel.kickAfterUnit) {
      errors.push("Kick after unit is required when kickUsers is enabled");
    }
  }

  // Validate monitoring configuration
  if (channel.monitoringEnabled) {
    if (!channel.alertsType) {
      errors.push("Alerts type is required when monitoring is enabled");
    }
    if (channel.monitoringConfig) {
      const config = channel.monitoringConfig;
      if (
        config.confidenceThreshold !== undefined &&
        (config.confidenceThreshold < 0 || config.confidenceThreshold > 1)
      ) {
        errors.push("Confidence threshold must be between 0 and 1");
      }
      if (
        config.messagesCacheDuration !== undefined &&
        config.messagesCacheDuration < 1
      ) {
        errors.push("Messages cache duration must be at least 1 day");
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
